import { useState, useEffect } from 'react'
import { router, Link } from '@inertiajs/react'
import {
  BuildingIcon,
  MapPinIcon,
  PencilLineIcon,
  PhoneIcon,
  PlusIcon,
  SearchIcon,
  Trash2Icon,
  UsersIcon,
  XIcon,
} from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import { DeleteConfirmationDialog } from '@/components/delete-confirmation-dialog'

interface Site {
  id: number
  name: string
  address_line1: string | null
  address_line2: string | null
  city: string | null
  state: string | null
  zip: string | null
  phone_number: string | null
  full_address: string
  location_name: string
  patient_count: number
  created_at: string
}

interface PaginationData {
  page: number
  pages: number
  count: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

interface Filters {
  search?: string | null
  sort?: string
  direction?: string
}

interface AdminSitesIndexProps {
  sites: Site[]
  pagination: PaginationData
  filters: Filters
}

export default function AdminSitesIndex({ sites, pagination, filters }: AdminSitesIndexProps) {
  const [searchTerm, setSearchTerm] = useState(filters.search || '')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [siteToDelete, setSiteToDelete] = useState<Site | null>(null)

  useEffect(() => {
    const timer = setTimeout(() => {
      const currentFilter = filters.search || ''
      if (searchTerm !== currentFilter) {
        router.get('/admin/sites', {
          search: searchTerm || undefined,
          sort: filters.sort,
          direction: filters.direction,
          page: 1,
        }, { preserveState: true, preserveScroll: true, only: ['sites', 'pagination', 'filters'] })
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  const handleSort = (column: string) => {
    const newDirection =
      filters.sort === column && filters.direction === 'asc' ? 'desc' : 'asc'
    router.get('/admin/sites', {
      search: filters.search,
      sort: column,
      direction: newDirection,
      page: 1,
    }, { preserveState: true, preserveScroll: true, only: ['sites', 'pagination', 'filters'] })
  }

  const handlePageChange = (page: number) => {
    router.get('/admin/sites', {
      search: filters.search,
      sort: filters.sort,
      direction: filters.direction,
      page,
    }, { preserveState: true, preserveScroll: true, only: ['sites', 'pagination', 'filters'] })
  }

  const handleDelete = (site: Site) => {
    setSiteToDelete(site)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = () => {
    if (!siteToDelete) return
    router.delete(`/admin/sites/${siteToDelete.id}`, {
      onSuccess: () => {
        setDeleteDialogOpen(false)
        setSiteToDelete(null)
      },
    })
  }

  const renderPageNumbers = () => {
    const pages = []
    const totalPages = pagination.pages
    const currentPage = pagination.page

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i)
    } else {
      pages.push(1)
      if (currentPage > 3) pages.push('ellipsis-start')
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i)
      }
      if (currentPage < totalPages - 2) pages.push('ellipsis-end')
      pages.push(totalPages)
    }
    return pages
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col gap-2">
        <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
          <div className="px-4 lg:px-6">
            <Card className="min-w-0">
              <CardHeader>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <BuildingIcon className="h-5 w-5 text-primary" />
                      Clinic Sites
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      {pagination.count} {pagination.count === 1 ? 'site' : 'sites'} total
                    </p>
                  </div>
                  <Button asChild>
                    <Link href="/admin/sites/new">
                      <PlusIcon className="h-4 w-4 mr-2" />
                      Add Site
                    </Link>
                  </Button>
                </div>

                {/* Search */}
                <div className="relative mt-4 max-w-sm">
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, city, or state..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-10"
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => setSearchTerm('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <XIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </CardHeader>

              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead
                          className="cursor-pointer select-none hover:text-foreground"
                          onClick={() => handleSort('name')}
                        >
                          <span className="flex items-center gap-1">
                            Site Name
                            {filters.sort === 'name' && (
                              <span className="text-xs">{filters.direction === 'asc' ? '↑' : '↓'}</span>
                            )}
                          </span>
                        </TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Patients</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sites.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="h-32 text-center text-muted-foreground">
                            {filters.search
                              ? 'No sites match your search.'
                              : 'No clinic sites yet. Add your first site to get started.'}
                          </TableCell>
                        </TableRow>
                      ) : (
                        sites.map((site) => (
                          <TableRow key={site.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 shrink-0">
                                  <BuildingIcon className="h-4 w-4 text-primary" />
                                </div>
                                <span className="font-medium">{site.name}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              {site.city || site.state ? (
                                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                  <MapPinIcon className="h-3.5 w-3.5 shrink-0" />
                                  <span>{[site.city, site.state].filter(Boolean).join(', ')}{site.zip ? ` ${site.zip}` : ''}</span>
                                </div>
                              ) : (
                                <span className="text-muted-foreground text-sm">—</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {site.phone_number ? (
                                <div className="flex items-center gap-1 text-sm">
                                  <PhoneIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                  <span>{site.phone_number}</span>
                                </div>
                              ) : (
                                <span className="text-muted-foreground text-sm">—</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="gap-1">
                                <UsersIcon className="h-3 w-3" />
                                {site.patient_count}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  aria-label="Edit"
                                  asChild
                                >
                                  <Link href={`/admin/sites/${site.id}/edit`}>
                                    <PencilLineIcon className="h-4 w-4" />
                                  </Link>
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  aria-label="Delete"
                                  onClick={() => handleDelete(site)}
                                  className="text-destructive hover:text-destructive"
                                >
                                  <Trash2Icon className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="flex items-center justify-between border-t px-4 py-3">
                    <p className="text-sm text-muted-foreground">
                      Showing {pagination.from}–{pagination.to} of {pagination.count}
                    </p>
                    <Pagination>
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious
                            onClick={() => pagination.prev && handlePageChange(pagination.prev)}
                            className={!pagination.prev ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                          />
                        </PaginationItem>
                        {renderPageNumbers().map((page, idx) =>
                          typeof page === 'string' ? (
                            <PaginationItem key={`${page}-${idx}`}>
                              <PaginationEllipsis />
                            </PaginationItem>
                          ) : (
                            <PaginationItem key={page}>
                              <PaginationLink
                                isActive={page === pagination.page}
                                onClick={() => handlePageChange(page)}
                                className="cursor-pointer"
                              >
                                {page}
                              </PaginationLink>
                            </PaginationItem>
                          )
                        )}
                        <PaginationItem>
                          <PaginationNext
                            onClick={() => pagination.next && handlePageChange(pagination.next)}
                            className={!pagination.next ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={confirmDelete}
        title="Delete Site"
        description={`Are you sure you want to delete "${siteToDelete?.name}"? This cannot be undone. Sites with associated patients cannot be deleted.`}
      />
    </div>
  )
}
